import { useEffect, useState } from 'react';
import Section from '../shared/Section';
import Field from '../shared/Field';
import Input from '../shared/Input';
import Btn from '../shared/Btn';
import Chip from '../shared/Chip';
import Grid from '../shared/Grid';
import Select from '../shared/Select';
import Modal from '../shared/Modal';
import Icon from '../shared/Icon';
import {
  createBillingProvider,
  deleteBillingProvider,
  listBillingProviders,
  updateBillingProvider,
} from '../../../api/claimsClient';

const EMPTY_PROFILE = {
  id: '',
  organization_name: '',
  first_name: '',
  last_name: '',
  middle_name: '',
  name_suffix: '',
  npi: '',
  tax_id: '',
  tax_id_qualifier: 'EI',
  taxonomy_code: '',
  additional_id: '',
  additional_id_qualifier: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  zip_code: '',
  phone: '',
  is_default: false,
};

const EMPTY_SERVICE_FACILITY = {
  organization_name: '',
  npi: '',
  other_id: '',
  other_id_qualifier: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  zip_code: '',
};

/**
 * Claim-level rendering NPI is UI-derived: shared NPI across populated
 * service lines (or null when "mixed").
 * @param {Array<any>|undefined} serviceLines
 */
function deriveClaimLevelNpi(serviceLines) {
  const populated = (serviceLines || []).map((/** @type {any} */ l) => l.rendering_provider_npi).filter(Boolean);
  if (populated.length === 0) return '';
  const first = populated[0];
  return populated.every((/** @type {string} */ n) => n === first) ? first : null;
}

/** @param {any} profile */
function profileToBillingSnapshot(profile) {
  return {
    organization_name: profile.organization_name || '',
    first_name: profile.first_name || '',
    last_name: profile.last_name || '',
    npi: profile.npi || '',
    tax_id: profile.tax_id || '',
    tax_id_qualifier: profile.tax_id_qualifier || 'EI',
    taxonomy_code: profile.taxonomy_code || '',
    address_line_1: profile.address_line_1 || '',
    address_line_2: profile.address_line_2 || '',
    city: profile.city || '',
    state: profile.state || '',
    zip_code: profile.zip_code || '',
    phone: profile.phone || '',
  };
}

/** @param {any} billing */
function profileDraftFromBilling(billing) {
  return {
    ...EMPTY_PROFILE,
    organization_name: billing.organization_name || '',
    first_name: billing.first_name || '',
    last_name: billing.last_name || '',
    npi: billing.npi || '',
    tax_id: billing.tax_id || '',
    tax_id_qualifier: billing.tax_id_qualifier || 'EI',
    taxonomy_code: billing.taxonomy_code || '',
    address_line_1: billing.address_line_1 || '',
    address_line_2: billing.address_line_2 || '',
    city: billing.city || '',
    state: billing.state || '',
    zip_code: billing.zip_code || '',
    phone: billing.phone || '',
  };
}

/** @param {any} profile */
function providerLabel(profile) {
  const name = profile.organization_name || [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Unnamed provider';
  return profile.npi ? `${name} · NPI ${profile.npi}` : name;
}

/**
 * @param {{
 *   open: boolean,
 *   profile: any,
 *   saving: boolean,
 *   onClose: () => void,
 *   onSave: (profile: any) => void,
 * }} props
 */
function ProviderProfileEditor({ open, profile, saving, onClose, onSave }) {
  const [draft, setDraft] = useState(profile || EMPTY_PROFILE);

  useEffect(() => {
    setDraft(profile || EMPTY_PROFILE);
  }, [profile]);

  /** @param {string} key @param {any} value */
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const isNew = !draft?.id;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isNew ? 'New provider profile' : 'Edit provider profile'}
      width={800}
      footer={(
        <>
          <Btn kind="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn kind="primary" onClick={() => onSave(draft)} disabled={saving}>
            {saving ? 'Saving…' : 'Save provider'}
          </Btn>
        </>
      )}
    >
      <Grid cols={2}>
        <Field label="Organization name">
          <Input
            value={draft.organization_name || ''}
            onChange={(/** @type {any} */ e) => set('organization_name', e.target.value)}
          />
        </Field>
        <Field label="NPI">
          <Input
            value={draft.npi || ''}
            onChange={(/** @type {any} */ e) => set('npi', e.target.value)}
            maxLength={10}
          />
        </Field>
        <Field label="First name">
          <Input
            value={draft.first_name || ''}
            onChange={(/** @type {any} */ e) => set('first_name', e.target.value)}
          />
        </Field>
        <Field label="Last name">
          <Input
            value={draft.last_name || ''}
            onChange={(/** @type {any} */ e) => set('last_name', e.target.value)}
          />
        </Field>
        <Field label="Tax ID qualifier">
          <Select
            value={draft.tax_id_qualifier || 'EI'}
            onChange={(value) => set('tax_id_qualifier', value)}
            options={[
              { value: 'EI', label: 'EIN' },
              { value: 'SY', label: 'SSN' },
            ]}
          />
        </Field>
        <Field label="Tax ID">
          <Input
            value={draft.tax_id || ''}
            onChange={(/** @type {any} */ e) => set('tax_id', e.target.value)}
          />
        </Field>
        <Field label="Taxonomy code">
          <Input
            value={draft.taxonomy_code || ''}
            onChange={(/** @type {any} */ e) => set('taxonomy_code', e.target.value)}
          />
        </Field>
        <Field label="Additional ID">
          <Input
            value={draft.additional_id || ''}
            onChange={(/** @type {any} */ e) => set('additional_id', e.target.value)}
          />
        </Field>
        <Field label="Address line 1">
          <Input
            value={draft.address_line_1 || ''}
            onChange={(/** @type {any} */ e) => set('address_line_1', e.target.value)}
          />
        </Field>
        <Field label="Address line 2">
          <Input
            value={draft.address_line_2 || ''}
            onChange={(/** @type {any} */ e) => set('address_line_2', e.target.value)}
          />
        </Field>
        <Field label="City">
          <Input
            value={draft.city || ''}
            onChange={(/** @type {any} */ e) => set('city', e.target.value)}
          />
        </Field>
        <Field label="State">
          <Input
            value={draft.state || ''}
            onChange={(/** @type {any} */ e) => set('state', e.target.value.toUpperCase())}
            maxLength={2}
          />
        </Field>
        <Field label="Zip code">
          <Input
            value={draft.zip_code || ''}
            onChange={(/** @type {any} */ e) => set('zip_code', e.target.value)}
            maxLength={10}
          />
        </Field>
        <Field label="Phone">
          <Input
            value={draft.phone || ''}
            onChange={(/** @type {any} */ e) => set('phone', e.target.value)}
          />
        </Field>
      </Grid>
    </Modal>
  );
}

/**
 * @param {{
 *   claim: any,
 *   setField: (path: string, value: any) => void,
 *   setMany: (updates: any) => void,
 *   editable: boolean,
 *   errorsByField: Map<string, any>,
 *   onAlert?: (msg: string, sev?: string) => void,
 * }} props
 */
export default function ProviderSection({ claim, setField, setMany, editable, errorsByField, onAlert }) {
  const billing = claim.billing_provider_snapshot || {};
  const serviceFacility = claim.service_facility_snapshot || {};
  const claimLevelNpi = deriveClaimLevelNpi(claim.service_lines);
  const [draftRenderingNpi, setDraftRenderingNpi] = useState(claimLevelNpi || '');
  const [showServiceFacility, setShowServiceFacility] = useState(Boolean(claim.service_facility_snapshot));
  const [defaultsLoading, setDefaultsLoading] = useState(false);
  const [profiles, setProfiles] = useState(/** @type {Array<any>} */ ([]));
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(/** @type {any|null} */ (null));
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileDeleting, setProfileDeleting] = useState(false);

  useEffect(() => {
    let mounted = true;
    setProfilesLoading(true);
    listBillingProviders()
      .then((next) => {
        if (!mounted) return;
        setProfiles(next);
        if (!selectedProfileId && billing.npi) {
          const match = next.find((/** @type {any} */ p) => p.npi && p.npi === billing.npi);
          if (match) setSelectedProfileId(match.id);
        }
      })
      .catch(() => {
        if (mounted) onAlert?.('Saved provider profiles could not be loaded.', 'warning');
      })
      .finally(() => {
        if (mounted) setProfilesLoading(false);
      });
    return () => { mounted = false; };
    // Load once when the Provider step mounts; claim edits should not refetch profiles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** @param {any} profile */
  const applyProfile = (profile) => {
    setMany({
      billing_provider_snapshot: profileToBillingSnapshot(profile),
    });
  };

  /** @param {string} profileId */
  const selectProfile = (profileId) => {
    setSelectedProfileId(profileId);
    const profile = profiles.find((/** @type {any} */ p) => p.id === profileId);
    if (profile) applyProfile(profile);
  };

  /** @param {any} profile */
  const saveProfile = async (profile) => {
    setProfileSaving(true);
    try {
      const saved = profile.id
        ? await updateBillingProvider(profile.id, profile)
        : await createBillingProvider(profile);
      setProfiles((current) => {
        const exists = current.some((/** @type {any} */ p) => p.id === saved.id);
        const next = exists
          ? current.map((/** @type {any} */ p) => (p.id === saved.id ? saved : p))
          : [...current, saved];
        return next.sort((a, b) => providerLabel(a).localeCompare(providerLabel(b)));
      });
      setSelectedProfileId(saved.id);
      applyProfile(saved);
      setEditorOpen(false);
      setEditingProfile(null);
      onAlert?.('Provider profile saved and applied to this claim.', 'success');
    } finally {
      setProfileSaving(false);
    }
  };

  const deleteSelectedProfile = async () => {
    if (!selectedProfileId) return;
    const profile = profiles.find((/** @type {any} */ p) => p.id === selectedProfileId);
    const name = profile ? providerLabel(profile) : 'this provider profile';
    if (!window.confirm(`Delete ${name}? This will not change the current claim fields.`)) return;
    setProfileDeleting(true);
    try {
      await deleteBillingProvider(selectedProfileId);
      setProfiles((current) => current.filter((/** @type {any} */ p) => p.id !== selectedProfileId));
      setSelectedProfileId('');
      onAlert?.('Provider profile deleted. Current claim fields were left unchanged.', 'success');
    } finally {
      setProfileDeleting(false);
    }
  };

  const useDefault = async () => {
    setDefaultsLoading(true);
    try {
      const profiles = await listBillingProviders({ is_default: true });
      const profile = profiles[0];
      if (!profile) {
        const all = await listBillingProviders();
        if (all[0]) applyProfile(all[0]);
        else onAlert?.(
          'No practice billing provider profiles configured. Add one in Settings → Billing Providers.',
          'warning',
        );
      } else {
        applyProfile(profile);
      }
    } finally {
      setDefaultsLoading(false);
    }
  };

  /** @param {string} npi */
  const setAllRenderingNpi = (npi) => {
    setDraftRenderingNpi(npi);
    const next = (claim.service_lines || []).map((/** @type {any} */ line) => {
      if (!line.rendering_provider_npi || line.rendering_provider_npi === claimLevelNpi) {
        return { ...line, rendering_provider_npi: npi };
      }
      return line;
    });
    setField('service_lines', next);
  };

  const renderingDisplay = claimLevelNpi === null ? 'Mixed (see lines)' : claimLevelNpi;

  return (
    <Section
      icon="briefcase-medical" title="Billing & Rendering Provider"
      subtitle="Box 33 (billing) and Box 24J (rendering, per line)"
      action={
        editable && (
          <Btn kind="ghost" size="sm" icon="copy" onClick={useDefault} disabled={defaultsLoading}>
            {defaultsLoading ? 'Loading…' : 'Use practice default'}
          </Btn>
        )
      }
    >
      <div style={{
        marginBottom: 20,
        padding: 16,
        border: '1px solid var(--ab-border-hair)',
        borderRadius: 'var(--ab-radius-md)',
        background: 'var(--ab-bg)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 8,
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 280px', minWidth: 220 }}>
            <Field label="Saved provider profile" caption="Selecting a profile fills the claim fields below; later edits below stay claim-specific.">
              <Select
                value={selectedProfileId}
                onChange={selectProfile}
                disabled={!editable || profilesLoading}
                options={[
                  { value: '', label: profilesLoading ? 'Loading profiles…' : 'Select a saved profile' },
                  ...profiles.map((/** @type {any} */ profile) => ({
                    value: profile.id,
                    label: providerLabel(profile),
                  })),
                ]}
              />
            </Field>
          </div>
          {editable && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingBottom: 20 }}>
              <Btn
                kind="secondary"
                size="sm"
                icon="plus"
                aria-label="New provider profile"
                onClick={() => {
                  setEditingProfile(profileDraftFromBilling(billing));
                  setEditorOpen(true);
                }}
              >
                New
              </Btn>
              <Btn
                kind="ghost"
                size="sm"
                icon="pencil"
                aria-label="Edit provider profile"
                disabled={!selectedProfileId}
                onClick={() => {
                  const profile = profiles.find((/** @type {any} */ p) => p.id === selectedProfileId);
                  if (profile) {
                    setEditingProfile(profile);
                    setEditorOpen(true);
                  }
                }}
              >
                Edit
              </Btn>
              <Btn
                kind="ghost"
                size="sm"
                icon="trash-2"
                aria-label="Delete provider profile"
                disabled={!selectedProfileId || profileDeleting}
                onClick={deleteSelectedProfile}
              >
                Delete
              </Btn>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--ab-fg-subtle)', fontWeight: 'var(--ab-weight-semi)' }}>
        Billing Provider · Box 33
      </div>
      <Grid cols={2}>
        <Field label="Billing Provider">
          <Input
            data-field="billing_provider_snapshot.organization_name"
            value={billing.organization_name || `${billing.first_name || ''} ${billing.last_name || ''}`.trim()}
            onChange={(/** @type {any} */ e) => setField('billing_provider_snapshot.organization_name', e.target.value)}
            disabled={!editable}
          />
        </Field>
        <Field label="NPI" required caption="Box 33a" error={errorsByField?.get('billing_provider_snapshot.npi')?.message}>
          <Input
            data-field="billing_provider_snapshot.npi"
            value={billing.npi || ''}
            onChange={(/** @type {any} */ e) => setField('billing_provider_snapshot.npi', e.target.value)}
            disabled={!editable} maxLength={10}
          />
        </Field>
        <Field label="Tax ID (TIN)" caption="Box 25">
          <Input
            data-field="billing_provider_snapshot.tax_id"
            value={billing.tax_id || ''}
            onChange={(/** @type {any} */ e) => setField('billing_provider_snapshot.tax_id', e.target.value)}
            disabled={!editable}
          />
        </Field>
        <Field label="Tax ID qualifier">
          <Select
            data-field="billing_provider_snapshot.tax_id_qualifier"
            value={billing.tax_id_qualifier || 'EI'}
            onChange={(value) => setField('billing_provider_snapshot.tax_id_qualifier', value)}
            disabled={!editable}
            options={[
              { value: 'EI', label: 'EIN' },
              { value: 'SY', label: 'SSN' },
            ]}
          />
        </Field>
        <Field label="Taxonomy code">
          <Input
            data-field="billing_provider_snapshot.taxonomy_code"
            value={billing.taxonomy_code || ''}
            onChange={(/** @type {any} */ e) => setField('billing_provider_snapshot.taxonomy_code', e.target.value)}
            disabled={!editable}
          />
        </Field>
        <Field label="Address line 1">
          <Input
            data-field="billing_provider_snapshot.address_line_1"
            value={billing.address_line_1 || ''}
            onChange={(/** @type {any} */ e) => setField('billing_provider_snapshot.address_line_1', e.target.value)}
            disabled={!editable}
          />
        </Field>
        <Field label="Address line 2">
          <Input
            data-field="billing_provider_snapshot.address_line_2"
            value={billing.address_line_2 || ''}
            onChange={(/** @type {any} */ e) => setField('billing_provider_snapshot.address_line_2', e.target.value)}
            disabled={!editable}
          />
        </Field>
        <Field label="City">
          <Input
            data-field="billing_provider_snapshot.city"
            value={billing.city || ''}
            onChange={(/** @type {any} */ e) => setField('billing_provider_snapshot.city', e.target.value)}
            disabled={!editable}
          />
        </Field>
        <Field label="State">
          <Input
            data-field="billing_provider_snapshot.state"
            value={billing.state || ''}
            onChange={(/** @type {any} */ e) => setField('billing_provider_snapshot.state', e.target.value)}
            disabled={!editable} maxLength={2}
          />
        </Field>
        <Field label="Zip code">
          <Input
            data-field="billing_provider_snapshot.zip_code"
            value={billing.zip_code || ''}
            onChange={(/** @type {any} */ e) => setField('billing_provider_snapshot.zip_code', e.target.value)}
            disabled={!editable} maxLength={10}
          />
        </Field>
        <Field label="Phone">
          <Input
            data-field="billing_provider_snapshot.phone"
            value={billing.phone || ''}
            onChange={(/** @type {any} */ e) => setField('billing_provider_snapshot.phone', e.target.value)}
            disabled={!editable}
          />
        </Field>
      </Grid>

      <div style={{ marginTop: 24, marginBottom: 8, fontSize: 12, color: 'var(--ab-fg-subtle)', fontWeight: 'var(--ab-weight-semi)' }}>
        Rendering Provider · Box 24J
      </div>
      <Grid cols={2}>
        <Field
          label="Rendering NPI (claim-level write-through)"
          caption="Each service line inherits this NPI. Override per-line in Services."
        >
          <Input
            value={draftRenderingNpi}
            onChange={(/** @type {any} */ e) => setDraftRenderingNpi(e.target.value)}
            disabled={!editable}
            maxLength={10}
          />
        </Field>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Chip tone={claimLevelNpi === null ? 'pending' : (claimLevelNpi === billing.npi ? 'info' : 'draft')}>
            {claimLevelNpi === null ? 'Mixed' : (claimLevelNpi === billing.npi ? 'Same as billing' : 'Distinct')}
          </Chip>
          {editable && (
            <>
              <Btn kind="ghost" size="sm" onClick={() => setAllRenderingNpi(draftRenderingNpi)}>
                Set all
              </Btn>
              <Btn kind="ghost" size="sm" onClick={() => setAllRenderingNpi(billing.npi || '')}>
                Use billing NPI
              </Btn>
            </>
          )}
        </div>
      </Grid>
      <div style={{
        marginTop: 10,
        padding: '10px 14px',
        background: 'var(--ab-bg-alt)',
        border: '1px solid var(--ab-border-hair)',
        borderRadius: 'var(--ab-radius-sm)',
        fontSize: 12,
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
      }}>
        <Icon name="info" size={14} style={{ color: 'var(--ab-fg-subtle)', flexShrink: 0, marginTop: 1 }} />
        <span>
          The rendering NPI above prefills Box 24J on every service line. If multiple clinicians rendered
          services on this claim, override the NPI per-line in the Service Lines table.
        </span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)', marginTop: 8 }}>
        Current claim-level rendering NPI: <code>{renderingDisplay || '—'}</code>
      </div>

      <div style={{
        marginTop: 24,
        paddingTop: 18,
        borderTop: '1px solid var(--ab-border-hair)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: showServiceFacility ? 12 : 0,
        }}>
          <div>
            <div style={{
              fontSize: 12,
              color: 'var(--ab-fg-subtle)',
              fontWeight: 'var(--ab-weight-semi)',
              marginBottom: 2,
            }}>
              Service Facility · Box 32
            </div>
            <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)' }}>
              Optional when services were performed somewhere other than the billing provider address.
            </div>
          </div>
          {editable && (
            showServiceFacility ? (
              <Btn
                kind="ghost"
                size="sm"
                icon="x"
                aria-label="Remove service facility"
                onClick={() => {
                  setShowServiceFacility(false);
                  setField('service_facility_snapshot', null);
                }}
              >
                Remove
              </Btn>
            ) : (
              <Btn
                kind="secondary"
                size="sm"
                icon="plus"
                aria-label="Add service facility"
                onClick={() => {
                  setShowServiceFacility(true);
                  setField('service_facility_snapshot', EMPTY_SERVICE_FACILITY);
                }}
              >
                Add service facility
              </Btn>
            )
          )}
        </div>

        {showServiceFacility && (
          <Grid cols={2}>
            <Field label="Facility name">
              <Input
                data-field="service_facility_snapshot.organization_name"
                value={serviceFacility.organization_name || ''}
                onChange={(/** @type {any} */ e) => setField('service_facility_snapshot.organization_name', e.target.value)}
                disabled={!editable}
              />
            </Field>
            <Field label="Facility NPI">
              <Input
                data-field="service_facility_snapshot.npi"
                value={serviceFacility.npi || ''}
                onChange={(/** @type {any} */ e) => setField('service_facility_snapshot.npi', e.target.value)}
                disabled={!editable}
                maxLength={10}
              />
            </Field>
            <Field label="Facility other ID">
              <Input
                data-field="service_facility_snapshot.other_id"
                value={serviceFacility.other_id || ''}
                onChange={(/** @type {any} */ e) => setField('service_facility_snapshot.other_id', e.target.value)}
                disabled={!editable}
              />
            </Field>
            <Field label="Facility other ID qualifier">
              <Input
                data-field="service_facility_snapshot.other_id_qualifier"
                value={serviceFacility.other_id_qualifier || ''}
                onChange={(/** @type {any} */ e) => setField('service_facility_snapshot.other_id_qualifier', e.target.value)}
                disabled={!editable}
              />
            </Field>
            <Field label="Facility address line 1">
              <Input
                data-field="service_facility_snapshot.address_line_1"
                value={serviceFacility.address_line_1 || ''}
                onChange={(/** @type {any} */ e) => setField('service_facility_snapshot.address_line_1', e.target.value)}
                disabled={!editable}
              />
            </Field>
            <Field label="Facility address line 2">
              <Input
                data-field="service_facility_snapshot.address_line_2"
                value={serviceFacility.address_line_2 || ''}
                onChange={(/** @type {any} */ e) => setField('service_facility_snapshot.address_line_2', e.target.value)}
                disabled={!editable}
              />
            </Field>
            <Field label="Facility city">
              <Input
                data-field="service_facility_snapshot.city"
                value={serviceFacility.city || ''}
                onChange={(/** @type {any} */ e) => setField('service_facility_snapshot.city', e.target.value)}
                disabled={!editable}
              />
            </Field>
            <Field label="Facility state">
              <Input
                data-field="service_facility_snapshot.state"
                value={serviceFacility.state || ''}
                onChange={(/** @type {any} */ e) => setField('service_facility_snapshot.state', e.target.value.toUpperCase())}
                disabled={!editable}
                maxLength={2}
              />
            </Field>
            <Field label="Facility zip code">
              <Input
                data-field="service_facility_snapshot.zip_code"
                value={serviceFacility.zip_code || ''}
                onChange={(/** @type {any} */ e) => setField('service_facility_snapshot.zip_code', e.target.value)}
                disabled={!editable}
                maxLength={10}
              />
            </Field>
          </Grid>
        )}
      </div>
      <ProviderProfileEditor
        open={editorOpen}
        profile={editingProfile}
        saving={profileSaving}
        onClose={() => {
          setEditorOpen(false);
          setEditingProfile(null);
        }}
        onSave={saveProfile}
      />
    </Section>
  );
}
